import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import NewsSection from './components/NewsSection';
import Team from './components/Team';
import Footer from './components/Footer';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ContactPage from './pages/ContactPage';
import CoordinatorPage from './pages/CoordinatorPage';
import TeamPage from './pages/TeamPage';
import LogoMeaningPage from './pages/LogoMeaningPage';
import MissionPage from './pages/MissionPage';
import VisionPage from './pages/VisionPage';
import MediaPage from './pages/MediaPage';
import { AuthProvider } from './context/AuthContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import EventsManager from './pages/admin/EventsManager';
import EventForm from './pages/admin/EventForm';
import AnnouncementsManager from './pages/admin/AnnouncementsManager';
import AnnouncementForm from './pages/admin/AnnouncementForm';
import TeamManager from './pages/admin/TeamManager';
import TeamForm from './pages/admin/TeamForm';
import BlogsManager from './pages/admin/BlogsManager';
import BlogForm from './pages/admin/BlogForm';
import MagazinesManager from './pages/admin/MagazinesManager';
import MagazineForm from './pages/admin/MagazineForm';
import PressManager from './pages/admin/PressManager';
import PressForm from './pages/admin/PressForm';
import MagazinePage from './pages/MagazinePage';
import SeedData from './pages/admin/SeedData';
import DataBackup from './pages/admin/DataBackup';
import ProtectedRoute from './components/admin/ProtectedRoute';

import logo from './assets/turet-logo.jpg';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // 2.5 saniye sonra yükleme ekranını kaldır
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2500);
        return () => clearTimeout(timer);
    }, []);

    const Home = () => (
        <>
            <Hero />
            <NewsSection />
        </>
    );

    return (
        <AuthProvider>
            <div className="bg-[#0f172a] text-gray-200 w-full overflow-hidden font-sans">
                <AnimatePresence>
                    {isLoading ? (
                        <motion.div
                            key="splash"
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }} // Minimal exit to prevent jitter
                            transition={{ duration: 0.5 }}
                            className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#1e293b] via-[#020617] to-black"
                        >
                            <motion.div
                                initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
                                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                transition={{ duration: 1.2, type: "spring", bounce: 0.5 }}
                                className="relative mb-12"
                            >
                                {/* Radial Pulse / Spreading Light */}
                                {[...Array(3)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ scale: 0.8, opacity: 0.5 }}
                                        animate={{ scale: 3, opacity: 0 }}
                                        transition={{
                                            duration: 2.5,
                                            repeat: Infinity,
                                            delay: i * 0.4,
                                            ease: "easeOut"
                                        }}
                                        className="absolute inset-0 bg-cyan-400/20 rounded-full blur-xl pointer-events-none"
                                    />
                                ))}

                                {/* Inner Glow */}
                                <motion.div
                                    animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                    className="absolute inset-0 bg-cyan-500/30 rounded-full blur-3xl scale-125 pointer-events-none"
                                />

                                {/* Main Logo */}
                                <img
                                    src={logo}
                                    alt="TURET Logo"
                                    className="w-64 h-64 rounded-full object-cover border-4 border-white/5 shadow-2xl relative z-10"
                                />

                                {/* Reflection Logo - Mobile'de gizle (performans/görsel hata önlemek için) */}
                                <div className="hidden md:block absolute top-full left-0 w-full h-full -mt-8 z-0 opacity-40 pointer-events-none">
                                    <img
                                        src={logo}
                                        alt="Reflection"
                                        className="w-64 h-64 rounded-full object-cover border-4 border-white/5 blur-[2px] scale-y-[-1] mask-image-gradient"
                                        style={{
                                            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 60%)',
                                            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 60%)'
                                        }}
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.8 }}
                                className="text-center mt-8 px-4"
                            >
                                <h1 className="text-4xl md:text-5xl font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 mb-4 drop-shadow-lg">
                                    MSKÜ TURET
                                </h1>
                                <div className="h-px w-20 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mb-4" />
                                <p className="text-gray-300 text-xs md:text-sm tracking-[0.2em] font-medium uppercase leading-relaxed max-w-lg mx-auto">
                                    Muğla Sıtkı Koçman Üniversitesi <br />
                                    <span className="text-cyan-400">Turist Rehberliği Topluluğu</span>
                                </p>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>

                {!isLoading && (
                    <>
                        <Routes>
                            {/* Public Routes - Wrapped in Fragment to keep Navbar/Footer */}
                            <Route path="/*" element={
                                <>
                                    <Navbar />
                                    <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/haberler" element={<NewsPage />} />
                                        <Route path="/etkinlikler" element={<NewsPage />} />
                                        <Route path="/duyurular" element={<NewsPage type="announcements" />} />
                                        <Route path="/kose-yazilari" element={<NewsPage type="blogs" />} />
                                        <Route path="/dergimiz" element={<MagazinePage />} />
                                        <Route path="/koordinator" element={<CoordinatorPage />} />
                                        <Route path="/vizyon" element={<VisionPage />} />
                                        <Route path="/misyon" element={<MissionPage />} />
                                        <Route path="/ekip/baskan" element={<TeamPage type="baskan" />} />
                                        <Route path="/ekip/yonetim" element={<TeamPage type="yonetim" />} />
                                        <Route path="/ekip/denetim" element={<TeamPage type="denetim" />} />
                                        <Route path="/logomuz" element={<LogoMeaningPage />} />
                                        <Route path="/medya/sosyal" element={<MediaPage type="sosyal" />} />
                                        <Route path="/medya/basin" element={<MediaPage type="basin" />} />
                                        <Route path="/haberler/:id" element={<NewsDetailPage type="events" />} />
                                        <Route path="/duyurular/:id" element={<NewsDetailPage type="announcements" />} />
                                        <Route path="/kose-yazilari/:id" element={<NewsDetailPage type="blogs" />} />
                                        <Route path="/iletisim" element={<ContactPage />} />
                                    </Routes>
                                    <Footer />
                                </>
                            } />

                            {/* Admin Routes */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={
                                <ProtectedRoute>
                                    <AdminLayout />
                                </ProtectedRoute>
                            }>
                                <Route index element={<Dashboard />} />
                                <Route path="events" element={<EventsManager />} />
                                <Route path="events/new" element={<EventForm />} />
                                <Route path="events/edit/:id" element={<EventForm />} />

                                <Route path="announcements" element={<AnnouncementsManager />} />
                                <Route path="announcements/new" element={<AnnouncementForm />} />
                                <Route path="announcements/edit/:id" element={<AnnouncementForm />} />

                                <Route path="team" element={<TeamManager />} />
                                <Route path="team/new" element={<TeamForm />} />
                                <Route path="team/edit/:id" element={<TeamForm />} />

                                <Route path="seed" element={<SeedData />} />

                                <Route path="blogs" element={<BlogsManager />} />
                                <Route path="blogs/new" element={<BlogForm />} />
                                <Route path="blogs/edit/:id" element={<BlogForm />} />

                                <Route path="magazines" element={<MagazinesManager />} />
                                <Route path="magazines/new" element={<MagazineForm />} />
                                <Route path="magazines/edit/:id" element={<MagazineForm />} />

                                <Route path="magazines/edit/:id" element={<MagazineForm />} />

                                <Route path="press" element={<PressManager />} />
                                <Route path="press/new" element={<PressForm />} />
                                <Route path="press/edit/:id" element={<PressForm />} />

                                <Route path="backup" element={<DataBackup />} />
                                {/* Future admin routes will go here */}
                            </Route>
                        </Routes>
                    </>
                )}
            </div>
        </AuthProvider>
    );
}

export default App;
